import { useEffect, useRef, useState } from 'react';

export type RevealState = 'armed' | 'in';

export interface RevealOptions {
  /** How much of the element must be on screen before it reveals. 0–1. */
  threshold?: number;
  /** Reveal a little before the element's top edge crosses the viewport. */
  rootMargin?: string;
  /** Reveal once and stop observing. Default true — this is a marketing page,
   *  not a toy; things should not flicker as you scroll back up. */
  once?: boolean;
}

/**
 * Whether it is safe (and worth it) to hide content before revealing it.
 *
 * If IntersectionObserver is missing, or the visitor has asked for reduced
 * motion, we never arm — content simply renders visible. The CSS default is
 * also visible, so a render without JS is never blank.
 */
function shouldArm(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof IntersectionObserver === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    } catch {
      return false;
    }
  }
  return true;
}

/**
 * Scroll-reveal primitive.
 *
 * Returns a ref to attach to the element, and a state string to spread onto
 * `data-reveal`. Pair with the `.reveal` class in `ui.module.css`.
 *
 *   const { ref, state } = useReveal<HTMLElement>();
 *   <section ref={ref} data-reveal={state} className={ui.reveal}>
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(options: RevealOptions = {}) {
  const { threshold = 0.14, rootMargin = '0px 0px -8% 0px', once = true } = options;
  const ref = useRef<T | null>(null);
  const [state, setState] = useState<RevealState>(() => (shouldArm() ? 'armed' : 'in'));

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!shouldArm()) {
      setState('in');
      return;
    }

    // Already on screen at mount (deep-linked, short page) — reveal immediately
    // on the next frame so the transition still plays rather than snapping.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setState('in');
            if (once) observer.disconnect();
          } else if (!once) {
            setState('armed');
          }
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, state } as const;
}

/**
 * Reduced-motion flag for the handful of places where a decorative element is
 * better removed from the DOM than animated at zero duration.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return reduced;
}
